/**
 * Web Bluetooth readiness and the proven Leica indication transport.
 *
 * A Leica distance event is never a Room Sketch measurement until the next,
 * separate capture/review stage explicitly validates and routes it to an armed
 * field target.
 */
export const LEICA_DISTANCE_SERVICE = '3ab10100-f831-4395-b29d-570977d5bf94';
export const LEICA_DISTANCE_CHARACTERISTIC = '3ab10101-f831-4395-b29d-570977d5bf94';

export function bluetoothReadiness(navigatorLike = globalThis.navigator ?? {}) {
  if (typeof navigatorLike?.bluetooth?.requestDevice !== 'function') {
    return { available: false, message: 'This browser does not expose Web Bluetooth. Use the Lenovo tablet in Chrome for the capability test.' };
  }
  return { available: true, message: 'Ready to request a Bluetooth device. Select the DISTO only when it is awake.' };
}

export function describeBluetoothSelection(device) {
  const name = String(device?.name ?? '').trim() || 'unnamed Bluetooth device';
  return `Selected ${name}.`;
}

/**
 * Returns a device this origin already has permission to use, without opening
 * the browser picker. Prefer the exact last-selected id; the name fallback
 * covers DISTOs granted before Room Sketch began remembering that id.
 */
export async function getPermittedBluetoothDevice(navigatorLike = globalThis.navigator ?? {}, preferredId = '') {
  const bluetooth = navigatorLike?.bluetooth;
  if (typeof bluetooth?.getDevices !== 'function') return null;
  const devices = await bluetooth.getDevices();
  if (!Array.isArray(devices) || devices.length === 0) return null;
  const exact = preferredId ? devices.find((device) => device?.id === preferredId) : null;
  if (exact) return exact;
  return devices.find((device) => /(?:disto|leica)/i.test(String(device?.name ?? ''))) ?? null;
}

export async function inspectGattServices(device) {
  if (!device?.gatt || typeof device.gatt.connect !== 'function') throw new Error('The selected device does not provide a GATT connection.');
  const server = device.gatt.connected ? device.gatt : await device.gatt.connect();
  if (typeof server.getPrimaryServices !== 'function') throw new Error('The Bluetooth device connected but does not expose primary services.');
  const services = await server.getPrimaryServices();
  return { name: String(device.name ?? '').trim() || 'unnamed Bluetooth device', serviceUuids: services.map((service) => String(service.uuid)).sort() };
}

/** Decodes the candidate Leica format only for visible evidence; it writes no app state. */
export function decodeLeicaDistanceMeters(value) {
  if (!(value instanceof DataView) || value.byteLength !== 4) throw new Error('A Leica evidence notification must contain exactly a four-byte value.');
  const meters = value.getFloat32(0, true);
  if (!Number.isFinite(meters) || meters <= 0) throw new Error('The Leica evidence notification did not contain a positive finite distance.');
  return meters;
}

function rawHex(value) {
  return Array.from(new Uint8Array(value.buffer, value.byteOffset, value.byteLength), (byte) => byte.toString(16).padStart(2, '0')).join(' ');
}

/**
 * Candidate-service evidence only: subscribe to indications and hand the raw
 * bytes/decoded meters back to the UI. It neither creates nor updates a
 * measurement session, slot, candidate, confirmed value, or sketch.
 */
export async function subscribeLeicaDistanceEvidence(device, onEvidence) {
  if (!device?.gatt || typeof onEvidence !== 'function') throw new Error('Select a Bluetooth device and provide an evidence handler first.');
  const server = device.gatt.connected ? device.gatt : await device.gatt.connect();
  const service = await server.getPrimaryService(LEICA_DISTANCE_SERVICE);
  const characteristic = await service.getCharacteristic(LEICA_DISTANCE_CHARACTERISTIC);
  await characteristic.startNotifications();
  characteristic.addEventListener('characteristicvaluechanged', (event) => {
    const value = event?.target?.value;
    // platform-boundary-exception: a failed decode passes its message on as
    // diagnostic evidence beside the raw bytes, no retry-policy branch — see
    // platform/boundary-exceptions.json.
    try { onEvidence({ meters: decodeLeicaDistanceMeters(value), rawHex: rawHex(value) }); } catch (error) { onEvidence({ error: error.message, rawHex: value instanceof DataView ? rawHex(value) : 'unavailable' }); }
  });
  return characteristic;
}

/**
 * User-gesture picker restricted to Leica's documented distance service.
 *
 * A REEKON T1M in Bluetooth Keyboard/HID mode is intentionally not a Web
 * Bluetooth laser. Filtering here stops a designer from selecting the tape in
 * this control and mistaking Chrome's expected refusal for a bad pairing.
 */
export async function requestBluetoothDevice(navigatorLike = globalThis.navigator ?? {}) {
  const readiness = bluetoothReadiness(navigatorLike);
  if (!readiness.available) throw new Error(readiness.message);
  return navigatorLike.bluetooth.requestDevice({
    filters: [{ services: [LEICA_DISTANCE_SERVICE] }],
    optionalServices: ['generic_access'],
  });
}
